import type { SiteRow } from '../../hooks/usePipelineSnapshot';

// Section 3 of the spec: horizontally-scrolling card strip of sites with feed configs.

interface Props {
  sites: SiteRow[];
}

const STATUS_COLOR: Record<string, string> = {
  running: 'bg-blue-100 text-blue-700',
  sub_batch_processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  const seconds = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SitesFeedStrip({ sites }: Props) {
  if (!sites.length) {
    return (
      <div className="rounded-xl border bg-white shadow-sm p-4 text-sm text-gray-500">
        No feed import configurations found.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex gap-3 min-w-max">
        {sites.map(site => {
          const colorClass = STATUS_COLOR[site.latestRunStatus || ''] || 'bg-gray-100 text-gray-500';
          const isActive = site.activeRunCount > 0;
          return (
            <div
              key={`${site.siteId}-${site.configId}`}
              className={`rounded-xl border p-3 min-w-[220px] bg-white shadow-sm ${isActive ? 'ring-2 ring-blue-300' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{site.domain}</div>
                {isActive && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                    {site.activeRunCount} active
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                  {site.scheduleFrequency === 'manual' ? 'MANUAL' : 'SCHED'}
                </span>
                {site.scheduleFrequency !== 'manual' && site.nextRunAt && (
                  <span className="text-xs text-gray-500">
                    Next: {new Date(site.nextRunAt).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-gray-500">Last:</span>
                <span>{timeAgo(site.latestRunStartedAt)}</span>
                {site.latestRunStatus && (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${colorClass}`}>
                    {site.latestRunStatus}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
